import { Avatar, Box, Card, CardContent, Chip, Typography } from '@mui/material';
import React from 'react';
import { FaOptionIcon } from '@europrocurement/l2d-icons';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import { Offer, useOffer } from '../../hooks';

export type SelectOfferContentProps = {
    offers: Array<Offer>;
    selectedOfferId?: number;
    onSelectOffer: (offer?: Offer) => void;
};

const SelectOfferContent: React.FunctionComponent<SelectOfferContentProps> = function (props) {
    const { offers = [], selectedOfferId, onSelectOffer } = props;
    const { getOfferTypeColor, getOfferTypeLabel } = useOffer();

    return (
        <Box
            sx={{
                marginTop: '10px',
                display: 'flex',
                flexDirection: 'column',
                gap: '1em 0',
                minWidth: '500px',
            }}
        >
            <Typography variant="l2dh6">Sélection d&lsquo;une offre</Typography>
            <Card
                variant="outlined"
                data-testid="no-offer-choice"
                sx={{
                    margin: 0,
                    cursor: 'pointer',
                    bgcolor: !selectedOfferId ? 'grey.50' : 'background.paper',
                    border: !selectedOfferId ? 2 : 1,
                    borderColor: !selectedOfferId ? 'primary.main' : 'divider',
                    '&:hover': { boxShadow: 2 },
                }}
                onClick={() => onSelectOffer(undefined)}
            >
                <CardContent sx={{ paddingTop: 0, paddingBottom: '0px !important' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ bgcolor: 'grey.100' }}>
                            <FaOptionIcon
                                icon={['fasl', 'times']}
                                sx={{ color: 'grey.600' }}
                            />
                        </Avatar>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '0.5em',
                                alignItems: 'flex-start',
                                flexGrow: 1,
                            }}
                        >
                            <Typography
                                variant="l2dh7"
                                fontWeight="500"
                            >
                                Aucune offre
                            </Typography>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                            >
                                Ne pas configurer d&lsquo;offre pour ce domaine
                            </Typography>
                        </Box>
                        {!selectedOfferId && (
                            <FaOptionIcon
                                icon={['fasl', 'check']}
                                sx={{ color: 'primary.main' }}
                            />
                        )}
                    </Box>
                </CardContent>
            </Card>
            <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
            >
                Offres disponibles
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1em 0' }}>
                {offers.map((offer) => {
                    const color = getOfferTypeColor(offer);

                    return (
                        <Card
                            key={offer.id}
                            variant="outlined"
                            data-testid="offer-choice"
                            sx={{
                                margin: 0,
                                cursor: 'pointer',
                                bgcolor:
                                    selectedOfferId === offer.id
                                        ? 'primary.50'
                                        : 'background.paper',
                                border: selectedOfferId === offer.id ? 2 : 1,
                                borderColor:
                                    selectedOfferId === offer.id ? `${color}.main` : 'divider',
                                '&:hover': { boxShadow: 2 },
                            }}
                            onClick={() => onSelectOffer(offer)}
                        >
                            <CardContent sx={{ paddingTop: 0, paddingBottom: '0px !important' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    {offer.icon && (
                                        <Avatar
                                            sx={{
                                                backgroundColor: `${color}.light`,
                                            }}
                                        >
                                            <FaOptionIcon
                                                icon={['fasl', offer.icon as IconName]}
                                                sx={{ color: `${color}.dark` }}
                                            />
                                        </Avatar>
                                    )}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '0.5em',
                                            alignItems: 'flex-start',
                                            flexGrow: 1,
                                        }}
                                    >
                                        <Typography
                                            variant="l2dh7"
                                            fontWeight="500"
                                        >
                                            {offer.libelle}
                                        </Typography>
                                        <Chip
                                            label={getOfferTypeLabel(offer)}
                                            color="default"
                                            size="small"
                                            sx={{
                                                mt: 0.5,
                                                backgroundColor: `${color}.light`,
                                                color: `${color}.dark`,
                                                fontSize: '12px',
                                                fontWeight: '500',
                                            }}
                                        />
                                    </Box>
                                    {selectedOfferId === offer.id && (
                                        <FaOptionIcon
                                            icon={['fasl', 'check']}
                                            sx={{ color: `${color}.main` }}
                                        />
                                    )}
                                </Box>
                            </CardContent>
                        </Card>
                    );
                })}
            </Box>
        </Box>
    );
};

export default SelectOfferContent;
